<template>
  <div class="lock-alert">
    <div
      class="headline"
      :class="{ 'headline--released': noActiveLocks }"
      role="status"
      aria-live="polite"
    >
      <template v-if="isAdmin && loading && hasUnlocked">
        所选占用已释放，正在确认当前占用情况…
      </template>
      <template v-else-if="isAdmin && loadError">
        {{
          hasUnlocked
            ? '所选占用已释放，当前占用情况仍需刷新确认。'
            : '暂时无法确认当前占用情况，请重新加载。'
        }}
      </template>
      <template v-else-if="noActiveLocks">
        当前数据集已无活跃占用，请关闭弹窗后重新尝试标注。
      </template>
      <template v-else-if="isAdmin && hasUnlocked">
        所选占用已释放，数据集仍有其他占用，请按需处理后重新尝试标注。
      </template>
      <template v-else>
        该数据正被 <b>{{ holderDisplay }}</b> 占用。
        <span v-if="isAdmin">请确认下方记录后按需解锁。</span>
        <span v-else>请稍后重试或联系管理员处理。</span>
      </template>
    </div>
    <div
      v-if="!holderText?.trim() && backendMessage && !hasUnlocked && !hasLoaded"
      class="metadata-note"
      >{{ backendMessage }}</div
    >

    <template v-if="isAdmin">
      <div class="list-head">
        <span class="list-title"
          >当前数据集占用<span v-if="hasLoaded && !loading && !loadError" class="count">{{
            rows.length
          }}</span></span
        >
        <button
          type="button"
          class="refresh-btn"
          :disabled="loading || busyId !== null || confirmingId !== null || !hasDataset"
          @click="refresh"
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            :class="{ spinning: loading }"
            aria-hidden="true"
          >
            <path
              d="M13 6a5.2 5.2 0 1 0 .2 3M13 2v4H9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ loading ? '刷新中…' : '刷新' }}
        </button>
      </div>
      <div v-if="!hasDataset" class="state-message"
        >未能确定数据集，请从数据集内容页的「占用管理」处理。</div
      >
      <div v-else-if="loadError" class="state-message state-message--error" role="alert">
        <span>{{ loadError }}</span>
        <button
          type="button"
          class="retry-btn"
          :disabled="loading || busyId !== null"
          @click="refresh"
          >重新加载</button
        >
      </div>
      <div v-else-if="loading && rows.length === 0" class="state-message" role="status"
        >正在加载占用记录…</div
      >
      <div v-else-if="noActiveLocks" class="state-message state-message--empty"
        >暂无活跃占用记录</div
      >
      <template v-else>
        <div v-if="nameLookupFailed" class="metadata-note"
          >部分人员信息暂时无法加载，已显示可核实的占用记录。</div
        >
        <div
          class="lock-list"
          tabindex="0"
          aria-label="当前数据集占用记录，可滚动查看全部记录"
          :aria-busy="loading"
        >
          <div
            v-for="row in rows"
            :key="row.id"
            class="lock-row"
            :class="{ 'lock-row--related': row.isHit }"
          >
            <div class="avatar" aria-hidden="true">{{ avatarChar(row.name) }}</div>
            <div class="who">
              <div class="name-line">
                <span class="name" :title="row.name">{{ row.name }}</span>
                <span
                  v-if="row.isHit"
                  class="related-tag"
                  title="根据占用提示中的姓名匹配，仅供参考"
                  >相关占用</span
                >
              </div>
              <div class="meta-line">
                <span>{{ row.itemTypeText }}</span>
                <span v-if="row.lockedNum !== null">占用 {{ row.lockedNum }} 条</span>
                <span :title="row.timeText"
                  >开始于 <span class="mono">{{ row.timeText }}</span></span
                >
              </div>
              <div class="duration"
                >已持续 <span class="mono">{{ row.durationText }}</span></div
              >
            </div>
            <button
              type="button"
              class="unlock-btn"
              :disabled="loading || !!loadError || busyId !== null || confirmingId !== null"
              :aria-busy="busyId === row.id"
              :aria-label="'强制解锁 ' + row.name + ' 的占用记录'"
              @click="onUnlock(row)"
            >
              {{ busyId === row.id ? '解锁中…' : '强制解锁' }}
            </button>
          </div>
        </div>
      </template>
      <div class="tip"
        >强制解锁会释放所选记录关联的占用，可能涉及多条数据。已保存的标注保留；原占用者未保存的修改可能无法提交。</div
      >
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue';
  import { message } from 'ant-design-vue';
  import {
    adminListLocks,
    adminForceUnLock,
    getLockedRecordByDataset,
    unLockApi,
  } from '/@/api/business/dataset';
  import { useUserStore } from '/@/store/modules/user';
  import {
    confirmForceUnlock,
    formatLockDuration,
    formatLockTime,
    lockItemTypeText,
  } from '/@/utils/business/dataLock';

  interface LockRow {
    id: number;
    name: string;
    isHit: boolean;
    lockedNum: number | null;
    itemTypeText: string;
    timeText: string;
    durationText: string;
  }

  const props = defineProps<{
    holderText?: string;
    backendMessage?: string;
    datasetId?: number | string | null;
  }>();
  const userStore = useUserStore();
  // 数据源 /data/admin/locks 仍以 user:manage 鉴权，不扩展角色权限。
  const isAdmin = computed(() => userStore.hasPermission?.('user:manage') ?? false);
  const hasDataset = computed(
    () => Number(props.datasetId) > 0 && Number.isFinite(Number(props.datasetId)),
  );
  const loading = ref(false);
  const hasLoaded = ref(false);
  const loadError = ref('');
  const nameLookupFailed = ref(false);
  const busyId = ref<number | null>(null);
  const confirmingId = ref<number | null>(null);
  const hasUnlocked = ref(false);
  const rows = ref<LockRow[]>([]);
  const noActiveLocks = computed(
    () =>
      isAdmin.value &&
      hasLoaded.value &&
      !loading.value &&
      !loadError.value &&
      rows.value.length === 0,
  );
  const holderDisplay = computed(() => (props.holderText || '').trim() || '其他用户');
  const holderNames = computed(() =>
    (props.holderText || '')
      .split(/[、,，]/)
      .map((name) => name.trim())
      .filter(Boolean),
  );

  function avatarChar(name: string) {
    return name.startsWith('用户#') ? name.slice(3, 4) || '?' : name.slice(0, 1).toUpperCase();
  }

  async function refresh() {
    if (!isAdmin.value || !hasDataset.value || loading.value) return;
    loading.value = true;
    loadError.value = '';
    try {
      // 姓名失败仍展示真实锁记录及用户ID；锁记录失败不能降级成“无占用”。
      const [lockRows, nameRows] = await Promise.all([
        adminListLocks(Number(props.datasetId)),
        getLockedRecordByDataset({ datasetId: props.datasetId }).catch(() => null),
      ]);
      if (!Array.isArray(lockRows)) throw new Error('占用列表响应格式不正确');
      nameLookupFailed.value = !Array.isArray(nameRows);
      const nameMap = new Map<number, { name: string; lockedNum: number | null }>();
      const nameRecords = Array.isArray(nameRows) ? nameRows : [];
      nameRecords.forEach((record: any) => {
        if (record?.recordId == null) return;
        const count = record.lockedNum == null ? NaN : Number(record.lockedNum);
        nameMap.set(Number(record.recordId), {
          name: String(record.lockedBy || ''),
          lockedNum: Number.isInteger(count) && count >= 0 ? count : null,
        });
      });
      rows.value = lockRows
        .map((record: any) => {
          const meta = nameMap.get(Number(record.id));
          const name = meta?.name || `用户#${record.createdBy}`;
          return {
            id: record.id,
            name,
            // 昵称只能判定“相关”，不能宣称精确命中被点击的数据。
            isHit: holderNames.value.includes(name),
            lockedNum: meta?.lockedNum ?? null,
            itemTypeText: lockItemTypeText(record.itemType),
            timeText: formatLockTime(record.createdAt),
            durationText: formatLockDuration(record.createdAt) || '—',
          };
        })
        .sort((a, b) => Number(b.isHit) - Number(a.isHit));
      hasLoaded.value = true;
    } catch {
      // 保留上次成功的快照，但错误时不展示或允许对陈旧记录执行解锁。
      loadError.value = hasUnlocked.value
        ? '所选占用已解锁，但列表刷新失败，请重试确认当前占用情况。'
        : '占用记录加载失败，暂时无法确认当前占用情况。';
    } finally {
      loading.value = false;
    }
  }

  function onUnlock(row: LockRow) {
    if (
      !isAdmin.value ||
      loading.value ||
      loadError.value ||
      busyId.value !== null ||
      confirmingId.value !== null
    )
      return;
    confirmingId.value = row.id;
    confirmForceUnlock(
      async () => {
        if (busyId.value !== null) return;
        busyId.value = row.id;
        try {
          try {
            await adminForceUnLock(row.id);
          } catch {
            // 保留原有接口兜底，不更改解锁权限与后端流程。
            await unLockApi({ lockRecordIds: [row.id] });
          }
          hasUnlocked.value = true;
          rows.value = rows.value.filter((record) => record.id !== row.id);
          message.success('所选占用已解锁');
          await refresh();
        } catch (error: any) {
          message.error(error?.message || '解锁失败，请重试');
        } finally {
          busyId.value = null;
          confirmingId.value = null;
        }
      },
      {
        holderName: row.name,
        lockedNum: row.lockedNum,
        onCancel: () => {
          confirmingId.value = null;
        },
      },
    );
  }

  onMounted(refresh);
</script>

<style lang="less" scoped>
  // 与现有占用管理行卡片保持同一套字体、间距和平台令牌。
  .lock-alert {
    color: @fg-1;
    font-size: 13px;
    line-height: 1.6;
    min-width: 0;
  }
  .headline {
    padding: 10px 12px;
    border: 1px solid fade(@dr-warning, 24%);
    border-radius: @r-sm;
    background: @dr-warning-bg;
    overflow-wrap: anywhere;
    b {
      color: @fg-0;
      font-weight: 600;
    }
    &--released {
      background: fade(@dr-success, 8%);
      border-color: fade(@dr-success, 24%);
    }
  }
  .list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 16px 0 10px;
  }
  .list-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: @fg-0;
    font-weight: 600;
  }
  .count {
    padding: 0 6px;
    border-radius: @r-full;
    background: @surface-3;
    color: @fg-2;
    font-family: @font-mono;
    font-size: 11px;
    font-weight: 500;
  }
  .refresh-btn,
  .retry-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    flex-shrink: 0;
    border: 0;
    border-radius: @r-xs;
    padding: 2px 4px;
    background: transparent;
    color: @brand-400;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
    cursor: pointer;
    &:hover:not(:disabled) {
      color: @brand-500;
      background: fade(@brand-400, 8%);
    }
    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    svg {
      width: 14px;
      height: 14px;
    }
  }
  .lock-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 300px;
    // 保留浏览器的视口计算，避免 Less 提前把不同单位的 min/max 折叠成 120px。
    max-height: ~'min(300px, max(120px, calc(100vh - 380px)))';
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 2px;
    scrollbar-gutter: stable;
  }
  .lock-row {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border: 1px solid @line-1;
    border-radius: @r-md;
    background: @surface-2;
    &--related {
      border-color: fade(@dr-warning, 32%);
      background: fade(@dr-warning, 5%);
    }
  }
  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    border-radius: @r-full;
    background: fade(@brand-400, 12%);
    color: @brand-500;
    font-size: 13px;
    font-weight: 600;
  }
  .who {
    flex: 1;
    min-width: 0;
  }
  .name-line {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: @fg-0;
    font-weight: 600;
  }
  .related-tag {
    flex-shrink: 0;
    border-radius: @r-xs;
    padding: 0 5px;
    background: @dr-warning-bg;
    color: @dr-warning;
    font-size: 10px;
    line-height: 18px;
    white-space: nowrap;
  }
  .meta-line {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 10px;
    margin-top: 3px;
    color: @fg-2;
    font-size: 11.5px;
    > span {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
  .duration {
    color: @fg-2;
    font-size: 11.5px;
  }
  .mono {
    font-family: @font-mono;
  }
  .unlock-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 88px;
    width: 88px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid fade(@dr-danger, 42%);
    border-radius: @r-sm;
    background: @dr-danger-bg;
    color: @dr-danger;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transition: background 100ms ease;
    &:hover:not(:disabled) {
      background: @dr-danger;
      color: #fff;
    }
    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }
  .unlock-btn:focus-visible,
  .refresh-btn:focus-visible,
  .retry-btn:focus-visible,
  .lock-list:focus-visible {
    box-shadow: 0 0 0 2px fade(@brand-400, 30%);
  }
  .state-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 12px;
    border: 1px dashed @line-1;
    border-radius: @r-md;
    color: @fg-2;
    text-align: center;
    font-size: 12px;
    &--error {
      color: @dr-danger;
      border-color: fade(@dr-danger, 24%);
    }
    &--empty {
      color: @fg-2;
    }
  }
  .metadata-note {
    color: @fg-2;
    font-size: 11.5px;
    margin-bottom: 8px;
  }
  .tip {
    margin-top: 12px;
    color: @fg-2;
    font-size: 11.5px;
    line-height: 1.55;
  }
  .spinning {
    animation: lock-refresh-spin 1s linear infinite;
  }
  @keyframes lock-refresh-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (max-width: 480px) {
    .lock-row {
      padding: 10px;
      gap: 8px;
    }
    .avatar {
      display: none;
    }
    .related-tag {
      font-size: 9px;
      padding: 0 4px;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .spinning {
      animation: none;
    }
  }
</style>
